/**
 * Wave 6 — Source contribution CTA.
 *
 * Lightweight prompt that turns every investigation into a living archive:
 * "Know a source we missed? Submit a document, archived record, or
 * primary evidence."
 *
 * Routes externally to the Shopify-hosted contribution form (where the
 * methodology, evidence-philosophy, and contribution pages already live).
 * Keeps the platform feeling like one coherent system without burning
 * credits on a full submission backend.
 */
import React from "react";
import { FileSearch, ArrowUpRight } from "lucide-react";

const CONTRIBUTE_URL = process.env.REACT_APP_SOURCE_LEAD_URL
    || "https://thecontextproject.com/pages/sources#contribute";

export default function SourceLeadCTA({ slug, className = "" }) {
    return (
        <div
            data-testid="source-lead-cta"
            className={`relative rounded-xl border border-[#243044] bg-[#0B1220]/60 px-5 py-5 sm:px-6 sm:py-6 ${className}`}
        >
            <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#56CCF2]/10 border border-[#56CCF2]/20 flex items-center justify-center text-[#56CCF2]">
                    <FileSearch className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-1.5">
                        Contribute to this investigation
                    </div>
                    <h3 className="text-[15px] font-semibold text-white mb-1.5 leading-snug">
                        Know a source we missed?
                    </h3>
                    <p className="text-[13px] text-[#94A3B8] leading-relaxed mb-3 max-w-xl">
                        Documents, archived records, peer-reviewed studies, or primary evidence — every verified submission strengthens the archive.
                    </p>
                    <a
                        href={slug ? `${CONTRIBUTE_URL}?investigation=${encodeURIComponent(slug)}` : CONTRIBUTE_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="source-lead-submit"
                        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#56CCF2] hover:text-white transition-colors"
                    >
                        Submit a source lead
                        <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                </div>
            </div>
        </div>
    );
}
